import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { loadStripe } from '@stripe/stripe-js';
import { StripeService, EnvironmentsService } from '@out-web/core';
import { firstValueFrom } from 'rxjs';

interface EmbeddedCheckout {
  mount: (element: string | HTMLElement) => Promise<void>;
  unmount: () => Promise<void>;
  destroy: () => Promise<void>;
}

@Component({
  selector: 'out-ads-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './ads-select.component.html',
  styleUrls: ['./ads-select.component.scss'],
})
export class AdsSelectComponent implements OnInit {
  paymentForm: FormGroup;
  isSubmitting = false;
  checkingPaymentStatus = false;
  finishedSelectingAds = false;
  stripe: any;
  elements: any;
  isLoading = false;
  errorMessage = '';
  private checkout?: EmbeddedCheckout;

  PRICING = {
    autocheck: { amount: 20, priceId: 'price_1R6cVg2BmsN6YOna2whsmrfy' },
    adManagement: { amount: 100, priceId: 'price_1R6cXg2BmsN6YOna7YW1vPUG' },
    hdVideo: { amount: 25, priceId: 'price_1RBGft2BmsN6YOnavWlhxZId' },
    youtubePublish: { amount: 25, priceId: 'price_1R6cYh2BmsN6YOnayAMq0amp' },
    youtubeBoost: {
      10: { amount: 100, priceId: 'price_1R6cZl2BmsN6YOnaVXck3Ojp' },
      20: { amount: 200, priceId: 'price_1R6cb32BmsN6YOnanQ0SfSbV' },
      30: { amount: 300, priceId: 'price_1R6cbR2BmsN6YOnaXfUjzAZ5' },
    },
    adSpend: {
      10: { amount: 100, priceId: 'price_1R6ccG2BmsN6YOnaptdG5eWT' },
      20: { amount: 200, priceId: 'price_1R6cci2BmsN6YOnaCm9g0CGS' },
      30: { amount: 300, priceId: 'price_1R6cdE2BmsN6YOnaJnJvbLFh' },
    },
    googleSetup: { amount: 35, priceId: 'price_1R6ceF2BmsN6YOnaekvaYMGX' },
    googleBudget: {
      10: { amount: 50, priceId: 'price_1R6ceu2BmsN6YOnapXkihzuz' },
      20: { amount: 100, priceId: 'price_1R6cfP2BmsN6YOnaqx8NawWK' },
      30: { amount: 150, priceId: 'price_1R6cg52BmsN6YOna7QBVI1Aw' },
    },
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private stripeService: StripeService,
    private cd: ChangeDetectorRef,
    private envService: EnvironmentsService
  ) {
    this.paymentForm = this.fb.group({
      createAd: { value: true, disabled: true },
      basicAd: { value: true, disabled: true },
      autocheck: { value: false, disabled: true },
      adManagement: { value: false, disabled: true },
      adSpend: { value: 0, disabled: true },
      hdVideo: { value: false, disabled: true },
      youtubePublish: { value: false, disabled: true },
      youtubeBoost: { value: 0 as 10 | 20 | 30 },
      googleAds: { value: false, disabled: true },
      googleBudget: { value: 0, disabled: true },
      noRefund: { value: false, disabled: false },
    });

    this.paymentForm.valueChanges.subscribe(() => {
      this.updateTotalPrice();
    });
  }

  async ngOnInit() {
    this.toggleAdCreation();
    this.stripe = await loadStripe('pk_live_Kxt2ezjESxY6U9zTI0Rr74gZ');
  }

  toggleAdCreation() {
    const enabled = this.paymentForm.controls['createAd'].value;
    Object.keys(this.paymentForm.controls).forEach((key) => {
      if (key !== 'createAd' && key !== 'noRefund') {
        // Add check for noRefund
        if (enabled) {
          this.paymentForm.controls[key].enable();
        } else {
          this.paymentForm.controls[key].disable();
          this.paymentForm.controls[key].setValue(
            key === 'basicAd'
              ? true
              : key === 'adSpend' ||
                key === 'youtubeBoost' ||
                key === 'googleBudget'
              ? 0
              : false
          );
        }
      }
    });
  }

  updateTotalPrice() {
    this.paymentForm.updateValueAndValidity({ emitEvent: false });
  }

  get totalPrice(): number {
    const values = this.paymentForm.value;
    let total = 47;

    if (values.autocheck) total += this.PRICING.autocheck.amount;
    if (values.adManagement) total += this.PRICING.adManagement.amount;

    // Fix adSpend calculation
    if (values.adSpend > 0 && values.adManagement) {
      total += this.PRICING.adSpend[values.adSpend as 10 | 20 | 30].amount;
    }

    if (values.hdVideo) total += this.PRICING.hdVideo.amount;
    if (values.youtubePublish) total += this.PRICING.youtubePublish.amount;

    if (
      values.youtubeBoost > 0 &&
      values.youtubePublish &&
      values.adManagement
    ) {
      total +=
        this.PRICING.youtubeBoost[values.youtubeBoost as 10 | 20 | 30].amount;
    }

    if (values.googleAds) {
      total += this.PRICING.googleSetup.amount;
      if (values.googleBudget > 0) {
        total +=
          this.PRICING.googleBudget[values.googleBudget as 10 | 20 | 30].amount;
      }
    }

    return total;
  }

  async submitForm() {
    if (this.isSubmitting || !this.paymentForm.valid) return;

    this.isSubmitting = true;

    try {
      const items = this.getSelectedItems();
      this.stripeService.createPaymentIntent(items).subscribe({
        next: async (response: any) => {
          // Set flag after cleanup and before new setup
          this.finishedSelectingAds = true;

          this.cd.detectChanges();

          await this.setupStripeElements(response.secret, response.sessionId);
        },
        error: (error) => {
          this.errorMessage = error.message;
          this.finishedSelectingAds = false;
        },
        complete: () => {
          this.isSubmitting = false;
        },
      });
    } catch (error: any) {
      this.errorMessage = error.message;
      this.isSubmitting = false;
      this.finishedSelectingAds = false;
    }
  }

  private getSelectedItems() {
    const listing_id = this.route.snapshot.paramMap.get('id');
    const values = this.paymentForm.getRawValue();
    const items = [
      {
        listingId: listing_id,
        quantity: 1,
        price: 'price_1R6cUk2BmsN6YOna9l0gruAg',
      },
    ];

    if (values.createAd) {
      // Add autocheck if selected
      if (values.autocheck) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.autocheck.priceId,
        });
      }

      // Add adManagement if selected
      if (values.adManagement) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.adManagement.priceId,
        });
      }

      // Add adSpend if selected
      if (values.adSpend > 0 && values.adManagement) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.adSpend[values.adSpend as 10 | 20 | 30].priceId,
        });
      }

      // Add hdVideo if selected
      if (values.hdVideo) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.hdVideo.priceId,
        });
      }

      // Add youtubePublish if selected
      if (values.youtubePublish) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.youtubePublish.priceId,
        });
      }

      // Add youtubeBoost if selected
      if (
        values.youtubeBoost > 0 &&
        values.youtubePublish &&
        values.adManagement
      ) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price:
            this.PRICING.youtubeBoost[values.youtubeBoost as 10 | 20 | 30]
              .priceId,
        });
      }

      // Add googleAds and budget if selected
      if (values.googleAds) {
        items.push({
          listingId: listing_id,
          quantity: 1,
          price: this.PRICING.googleSetup.priceId,
        });

        if (values.googleBudget > 0) {
          items.push({
            listingId: listing_id,
            quantity: 1,
            price:
              this.PRICING.googleBudget[values.googleBudget as 10 | 20 | 30]
                .priceId,
          });
        }
      }
    }

    return items;
  }

  goBack() {
    const listingId = this.route.snapshot.paramMap.get('id');
    this.router.navigate(['/your-tesla/edit/listing/images', listingId]);
  }

  private async setupStripeElements(clientSecret: string, sessionId: string) {
    try {
      if (!this.stripe) {
        this.errorMessage = 'Stripe not initialized';
        return;
      }

      // Initialize the embedded checkout
      this.checkout = await this.stripe.initEmbeddedCheckout({
        clientSecret,
        onComplete: async () => {
          // Wait a short time to allow Stripe to process the payment
          setTimeout(() => this.pollPaymentStatus(sessionId), 2000);
        },
      });

      // Mount the checkout
      if (this.checkout) {
        await this.checkout.mount('#payment-element');
      } else {
        throw new Error('Checkout is not initialized');
      }
    } catch (error: any) {
      console.error('Error setting up Stripe elements:', error);
      this.errorMessage = error.message || 'Failed to initialize payment form';
      await this.cleanupCheckout();
    }
  }

  private async cleanupCheckout() {
    try {
      if (this.checkout) {
        await this.checkout.destroy();
        this.checkout = undefined;
        console.log('Just unmounted checkout', this.checkout);
      }

      // Clear the payment element container
      const paymentElement = document.getElementById('payment-element');
      if (paymentElement) {
        paymentElement.innerHTML = ''; // Clear the container
        console.log('Just deleted payment-element', paymentElement.innerHTML);
      }

      // Reset state flags
      this.finishedSelectingAds = false;
      this.errorMessage = '';
    } catch (error) {
      console.error('Error cleaning up checkout:', error);
    }
  }

  async backToEdit() {
    await this.cleanupCheckout();
    this.paymentForm.enable();
  }

  private async pollPaymentStatus(sessionId: string) {
    this.checkingPaymentStatus = true;
    const listing_id = this.route.snapshot.paramMap.get('id');
    try {
      const response = await firstValueFrom(
        this.stripeService.confirmPayment(sessionId)
      );
      if (response.status === 'complete') {
        this.router.navigate(['/pay/success']);
      } else {
        this.router.navigate(['/pay/failure']);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      this.router.navigate(['/pay/failure' + listing_id]);
    } finally {
      await this.cleanupCheckout();
    }
  }
}
